/**
 * ProjectDetailsStats Component
 *
 * Renders project statistics in a compact, scannable format.
 * - Desktop: Card with 3-column grid
 * - Mobile: Horizontal scrollable pills
 *
 * Stats shown:
 * - Completion percentage with progress bar
 * - Tasks completed count
 * - Days remaining (with overdue state)
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Progress } from 'antd';
import { link, border } from '../../configs/designTokens';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import '../../styles/index.scss';

const ProjectDetailsStats = ({ project, isMobile }) => {
  const stats = useMemo(() => {
    if (!project || !project.tasks) {
      return {
        totalTasks: 0,
        completedTasks: 0,
        completionPercentage: 0,
        daysLeft: null,
        isOverdue: false,
      };
    }

    const totalTasks = project.tasks.length;
    const completedTasks = project.tasks.filter(
      (task) => task.status === 'COMPLETED' || task.status === 'DONE'
    ).length;
    const completionPercentage = totalTasks > 0
      ? Math.round((completedTasks / totalTasks) * 100)
      : 0;

    let daysLeft = null;
    let isOverdue = false;
    if (project.endDate) {
      const today = moment();
      const endDate = moment(project.endDate);
      daysLeft = endDate.diff(today, 'days');
      isOverdue = daysLeft < 0;
    }

    return {
      totalTasks,
      completedTasks,
      completionPercentage,
      daysLeft,
      isOverdue,
    };
  }, [project]);

  // Mobile: Horizontal scrollable pills
  if (isMobile) {
    return (
      <div className="project-stats project-stats--mobile">
        <div className="project-stats__scroll">
          {/* Completion Pill */}
          <div className="project-stats__pill">
            <div className="project-stats__pill-icon project-stats__pill-icon--primary">
              <CheckCircleOutlined />
            </div>
            <div className="project-stats__pill-content">
              <span className="project-stats__pill-value">{stats.completionPercentage}%</span>
              <span className="project-stats__pill-label">Complete</span>
            </div>
            <Progress
              percent={stats.completionPercentage}
              showInfo={false}
              strokeColor={link.blue}
              trailColor={border.slate}
              size="small"
              className="project-stats__pill-progress"
            />
          </div>

          {/* Tasks Pill */}
          <div className="project-stats__pill">
            <div className="project-stats__pill-icon project-stats__pill-icon--success">
              <ClockCircleOutlined />
            </div>
            <div className="project-stats__pill-content">
              <span className="project-stats__pill-value">
                {stats.completedTasks}/{stats.totalTasks}
              </span>
              <span className="project-stats__pill-label">Tasks Done</span>
            </div>
          </div>

          {/* Days Left Pill */}
          <div className={`project-stats__pill ${stats.isOverdue ? 'project-stats__pill--danger' : ''}`}>
            <div className={`project-stats__pill-icon ${stats.isOverdue ? 'project-stats__pill-icon--danger' : 'project-stats__pill-icon--warning'}`}>
              <CalendarOutlined />
            </div>
            <div className="project-stats__pill-content">
              <span className="project-stats__pill-value">
                {stats.daysLeft !== null ? (stats.daysLeft >= 0 ? stats.daysLeft : Math.abs(stats.daysLeft)) : 'N/A'}
              </span>
              <span className="project-stats__pill-label">
                {stats.isOverdue ? 'Days Overdue' : 'Days Left'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop: Card grid
  return (
    <div className="project-stats project-stats--desktop">
      <div className="project-stats__grid">
        {/* Completion */}
        <div className="project-stats__card">
          <div className="project-stats__card-header">
            <CheckCircleOutlined className="project-stats__card-icon project-stats__card-icon--primary" />
            <span className="project-stats__card-label">Completion</span>
          </div>
          <div className="project-stats__card-value">{stats.completionPercentage}%</div>
          <Progress
            percent={stats.completionPercentage}
            showInfo={false}
            strokeColor={link.blue}
            trailColor={border.slate}
            className="project-stats__card-progress"
          />
        </div>

        {/* Tasks Completed */}
        <div className="project-stats__card">
          <div className="project-stats__card-header">
            <ClockCircleOutlined className="project-stats__card-icon project-stats__card-icon--success" />
            <span className="project-stats__card-label">Tasks Completed</span>
          </div>
          <div className="project-stats__card-value">{stats.completedTasks}</div>
          <div className="project-stats__card-sublabel">of {stats.totalTasks} total tasks</div>
        </div>

        {/* Days Left */}
        <div className={`project-stats__card ${stats.isOverdue ? 'project-stats__card--danger' : ''}`}>
          <div className="project-stats__card-header">
            <CalendarOutlined className={`project-stats__card-icon ${stats.isOverdue ? 'project-stats__card-icon--danger' : 'project-stats__card-icon--warning'}`} />
            <span className="project-stats__card-label">
              {stats.isOverdue ? 'Overdue' : 'Days Left'}
            </span>
          </div>
          <div className={`project-stats__card-value ${stats.isOverdue ? 'project-stats__card-value--danger' : ''}`}>
            {stats.daysLeft !== null ? (stats.daysLeft >= 0 ? stats.daysLeft : Math.abs(stats.daysLeft)) : 'N/A'}
          </div>
          {stats.isOverdue && (
            <div className="project-stats__card-sublabel project-stats__card-sublabel--danger">
              days overdue
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ProjectDetailsStats.propTypes = {
  project: PropTypes.shape({
    tasks: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string,
      })
    ),
    endDate: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
};

ProjectDetailsStats.defaultProps = {
  project: null,
  isMobile: false,
};

export default ProjectDetailsStats;
